import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop, LogIn, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AssetStatusBadge, AueBadge, DeviceStatusBadge, LoginEventBadge } from '../components/badges';
import { useToast } from '../components/toast';
import { Button, Card, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Tabs, Td, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dueDateInputToIso, formatDateTime, formatMinutes, formatRelative, LOGIN_EVENT_LABEL, personName } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import type { GoogleDeviceListItem, LoginEvent, LoginEventType, Page } from '../lib/types';

const PAGE_SIZE = 25;
const DEFAULTS = {
  tab: 'devices',
  search: '',
  status: '',
  orgUnitPath: '',
  model: '',
  linked: '',
  inactiveDays: '',
  aue: '',
  type: '',
  from: '',
  to: '',
  page: '1',
  sort: 'lastSync',
  dir: 'desc',
};

interface Filters {
  orgUnits: { value: string; count: number }[];
  statuses: { value: string; count: number }[];
  models: { value: string; count: number }[];
}

export function GoogleDevicesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const page = Number(filters.page) || 1;

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const options = useQuery({ queryKey: ['google-devices', 'filters'], queryFn: () => api.get<Filters>('/google-devices/filters') });

  const devices = useQuery({
    queryKey: ['google-devices', 'list', filters],
    enabled: filters.tab === 'devices',
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get<Page<GoogleDeviceListItem>>('/google-devices', {
        search: filters.search,
        status: filters.status,
        orgUnitPath: filters.orgUnitPath,
        model: filters.model,
        linked: filters.linked,
        inactiveDays: filters.inactiveDays,
        aue: filters.aue,
        sort: filters.sort,
        dir: filters.dir,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const events = useQuery({
    queryKey: ['google-devices', 'events', filters],
    enabled: filters.tab === 'activity',
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get<Page<LoginEvent>>('/login-events', {
        search: filters.search,
        type: filters.type,
        from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
        to: filters.to ? dueDateInputToIso(filters.to) : undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const sync = useMutation({
    mutationFn: () => api.post<{ alreadyQueued: boolean }>('/integrations/google/sync'),
    onSuccess: (result) => {
      toast.success(result.alreadyQueued ? 'A Google sync is already in progress' : 'Google sync started - refresh in a minute');
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const sortBy = (field: string) => setFilters({ sort: field, dir: filters.sort === field && filters.dir === 'desc' ? 'asc' : 'desc' });
  const sortable = (label: string, field: string) => (
    <button onClick={() => sortBy(field)} className={filters.sort === field ? 'text-slate-800 uppercase' : 'uppercase'}>
      {label}
      {filters.sort === field && (filters.dir === 'asc' ? ' ↑' : ' ↓')}
    </button>
  );

  return (
    <>
      <PageHeader
        title="ChromeOS devices"
        description="Devices, usage, and sign-in history from Google Admin."
        actions={
          can('TECHNICIAN') && (
            <Button icon={<RefreshCw />} loading={sync.isPending} onClick={() => sync.mutate()}>
              Sync now
            </Button>
          )
        }
      />

      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'devices', label: 'Devices', count: filters.tab === 'devices' ? devices.data?.total : undefined },
          { value: 'activity', label: 'Sign-in activity', count: filters.tab === 'activity' ? events.data?.total : undefined },
        ]}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={filters.tab === 'devices' ? 'Search serial, user, asset ID, MAC' : 'Search user email or serial'}
            className="w-full sm:w-72"
          />
          {filters.tab === 'devices' ? (
            <>
              <Select value={filters.orgUnitPath} onChange={(e) => setFilters({ orgUnitPath: e.target.value })} className="w-auto max-w-[14rem]">
                <option value="">All org units</option>
                {options.data?.orgUnits.map((ou) => (
                  <option key={ou.value} value={ou.value}>
                    {ou.value} ({ou.count})
                  </option>
                ))}
              </Select>
              <Select value={filters.model} onChange={(e) => setFilters({ model: e.target.value })} className="w-auto max-w-[14rem]">
                <option value="">All models</option>
                {options.data?.models.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.value} ({m.count})
                  </option>
                ))}
              </Select>
              <Select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} className="w-auto">
                <option value="">All statuses</option>
                {options.data?.statuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.value.charAt(0) + s.value.slice(1).toLowerCase()} ({s.count})
                  </option>
                ))}
              </Select>
              <Select value={filters.inactiveDays} onChange={(e) => setFilters({ inactiveDays: e.target.value })} className="w-auto">
                <option value="">Any last sync</option>
                <option value="7">Not synced in 7+ days</option>
                <option value="30">Not synced in 30+ days</option>
                <option value="90">Not synced in 90+ days</option>
              </Select>
              <Select value={filters.aue} onChange={(e) => setFilters({ aue: e.target.value })} className="w-auto">
                <option value="">Any auto-update date</option>
                <option value="expired">AUE expired</option>
                <option value="within_year">AUE within 12 months</option>
              </Select>
              <Select value={filters.linked} onChange={(e) => setFilters({ linked: e.target.value })} className="w-auto">
                <option value="">Linked or not</option>
                <option value="true">Linked to an asset</option>
                <option value="false">Not linked</option>
              </Select>
            </>
          ) : (
            <>
              <Select value={filters.type} onChange={(e) => setFilters({ type: e.target.value })} className="w-auto">
                <option value="">All events</option>
                {(['LOGIN', 'LOGOUT', 'LOGIN_FAILURE'] as LoginEventType[]).map((type) => (
                  <option key={type} value={type}>
                    {LOGIN_EVENT_LABEL[type]}
                  </option>
                ))}
              </Select>
              <Input type="date" value={filters.from} onChange={(e) => setFilters({ from: e.target.value })} className="w-auto" aria-label="From" />
              <span className="text-sm text-slate-400">to</span>
              <Input type="date" value={filters.to} onChange={(e) => setFilters({ to: e.target.value })} className="w-auto" aria-label="To" />
            </>
          )}
        </div>

        {filters.tab === 'devices' ? (
          devices.isLoading ? (
            <div className="flex h-60 items-center justify-center">
              <Spinner />
            </div>
          ) : devices.error ? (
            <ErrorState error={devices.error} onRetry={() => devices.refetch()} />
          ) : devices.data!.items.length === 0 ? (
            <EmptyState
              icon={<Laptop />}
              title="No devices found"
              description="Adjust the filters, or connect Google Workspace under Integrations to sync ChromeOS devices."
            />
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>{sortable('Serial / model', 'serialNumber')}</Th>
                    <Th>Status</Th>
                    <Th>{sortable('Recent user', 'mostRecentUser')}</Th>
                    <Th>{sortable('Org unit', 'orgUnitPath')}</Th>
                    <Th>{sortable('Last sync', 'lastSync')}</Th>
                    <Th>{sortable('Use (7d)', 'activeMinutes7d')}</Th>
                    <Th>{sortable('Auto-update', 'autoUpdateExpiration')}</Th>
                    <Th>Asset</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {devices.data!.items.map((device) => (
                    <tr key={device.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/devices/${device.id}`)}>
                      <Td className="max-w-[14rem]">
                        <Link to={`/devices/${device.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                          {device.serialNumber ?? device.deviceId}
                        </Link>
                        <p className="truncate text-xs text-slate-500">{device.model}</p>
                      </Td>
                      <Td>
                        <DeviceStatusBadge status={device.status} removed={!!device.removedFromGoogleAt} />
                      </Td>
                      <Td className="max-w-[14rem] truncate text-slate-700">{device.mostRecentUser ?? <span className="text-slate-400">—</span>}</Td>
                      <Td className="max-w-[12rem] truncate text-xs text-slate-500">{device.orgUnitPath}</Td>
                      <Td className="whitespace-nowrap" >
                        <span title={formatDateTime(device.lastSync)}>{formatRelative(device.lastSync)}</span>
                      </Td>
                      <Td className="whitespace-nowrap text-slate-600">{formatMinutes(device.activeMinutes7d)}</Td>
                      <Td className="whitespace-nowrap">
                        <AueBadge date={device.autoUpdateExpiration} />
                      </Td>
                      <Td className="whitespace-nowrap">
                        {device.asset ? (
                          <Link to={`/assets/${device.asset.id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold text-brand-800 hover:underline">{device.asset.assetTag}</span>
                            <AssetStatusBadge status={device.asset.status} />
                          </Link>
                        ) : (
                          <span className="text-xs text-slate-400">Not linked</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination page={page} pageSize={PAGE_SIZE} total={devices.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
            </>
          )
        ) : events.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : events.error ? (
          <ErrorState error={events.error} onRetry={() => events.refetch()} />
        ) : events.data!.items.length === 0 ? (
          <EmptyState icon={<LogIn />} title="No sign-in events" description="ChromeOS sign-ins are imported from the Google Admin audit log during each sync." />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Event</Th>
                  <Th>User</Th>
                  <Th>Device</Th>
                  <Th>IP address</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {events.data!.items.map((event) => (
                  <tr key={event.id}>
                    <Td className="whitespace-nowrap">{formatDateTime(event.occurredAt)}</Td>
                    <Td>
                      <LoginEventBadge type={event.eventType} />
                    </Td>
                    <Td className="max-w-[16rem]">
                      {event.person ? (
                        <Link to={`/people/${event.person.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                          {personName(event.person)}
                        </Link>
                      ) : null}
                      <p className="truncate text-xs text-slate-500">{event.userEmail ?? 'Unknown user'}</p>
                    </Td>
                    <Td>
                      {event.device ? (
                        <Link to={`/devices/${event.device.id}`} className="font-mono text-xs text-brand-800 hover:underline">
                          {event.device.serialNumber}
                        </Link>
                      ) : (
                        <span className="text-xs text-slate-500">{event.deviceName ?? '—'}</span>
                      )}
                      {event.device?.asset && <p className="text-xs text-slate-500">{event.device.asset.assetTag}</p>}
                    </Td>
                    <Td className="font-mono text-xs text-slate-500">{event.ipAddress ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={events.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
          </>
        )}
      </Card>
    </>
  );
}
